import { Endpoints } from "constants/endpoints";
import type {
  Personne,
  PersonneInfosPrivees,
  DelegueAssembleeGenerale,
  PersonneCreateDto,
  PersonneWithInfosPrivees,
} from "models";
import { elusApiClient } from "./apiClients";
import { SdeappsError } from "utils/errorHandling";
import { dateUtil } from "@sdeapps/react-core";
import type { Operation } from "fast-json-patch";

/**
 * Récupère toutes les personnes
 * @returns Array<Personne>
 */
async function getAllPersonnes(): Promise<Array<Personne>> {
  const url = Endpoints.PERSONNES;
  return elusApiClient.get(url);
}

/**
 * Récupère une personne à partir de son id
 * @param personneId l'id de la personne à chercher
 * @returns La personne dont l'id a été passé en paramètre
 */
async function getPersonneById(personneId: string): Promise<Personne> {
  const url = `/${Endpoints.PERSONNES}/${personneId}`;
  return elusApiClient.get(url);
}

/**
 * Récupère les données privées d'une personne à partir de son id
 * @param personneId l'id de la personne à chercher
 * @returns La personne dont l'id a été passé en paramètre
 */
async function getPersonneInfosPriveesById(personneId: string): Promise<PersonneInfosPrivees> {
  const url = `/${Endpoints.PERSONNES}/${personneId}/privees`;
  return elusApiClient.get(url);
}

/**
 * Récupère les personnes qui possèdent des mandats en cours liés à une Commune
 * @param codeCollectivite l'id de la Commune
 * @returns une liste de personnes
 */
async function getPersonnesByCommune(idCommune: string): Promise<Array<Personne>> {
  const url = `/${Endpoints.PERSONNES}/collectivites/commune/${idCommune}`;
  return elusApiClient.get(url);
}

/**
 * Récupère les personnes qui possèdent des mandats en cours liés à une Commune
 * @param codeCollectivite l'id de la Commune
 * @returns une liste de personnes
 */
async function getPersonnesByEpci(idEpci: string): Promise<Array<Personne>> {
  const url = `/${Endpoints.PERSONNES}/collectivites/epci/${idEpci}`;
  return elusApiClient.get(url);
}

/**
 * Crée un nouvel élu.
 * @param model Les informations de l'élu à créer.
 * @returns L'élu créé.
 */
async function create(model: PersonneCreateDto): Promise<Personne> {
  if (model.dateNaissance == null) {
    throw new SdeappsError("La date de naissance ne peut pas être null");
  }

  const data = {
    ...model,
    dateNaissance: dateUtil.format(model.dateNaissance, "yyyy-MM-dd"),
  };

  const url = Endpoints.PERSONNES;
  return elusApiClient.post(url, data);
}

/**
 * Modifie les données d'un élus
 * @param personneId l'identifiant de la personne à modifier
 * @param patchData la liste des propriétés à modifier
 * @returns la personne modifiée
 */
async function patchElu(personneId: string, patchData: Array<Operation>): Promise<Personne> {
  const url = `/${Endpoints.PERSONNES}/${personneId}/privees`;
  return elusApiClient.patch(url, patchData);
}

/**
 * Récupère les personnes qui ont des mandats de délégués à l'Assemblée Générale, avec seulement ces mandats.
 * @returns une liste de personnes et leurs informations privées
 */
async function getDeleguesAssembleeGenerale(): Promise<Array<DelegueAssembleeGenerale>> {
  const url = Endpoints.DELEGUES;
  return elusApiClient.get(url);
}

/**
 * Fusionne deux personnes en mettant toutes les fonctions SDEA et mandats institutionnels de la
 * personne à fusionner sur la personne à garder et supprimant la personne à fusionner.
 * @param idPersonneAGarder : id de la personne à garder
 * @param idPersonneAFusionner : id de la personne à fusionner
 * @returns PersonneWithInfosPrivees le résultat de la fusion
 */
async function fusionnerPersonnes(
  idPersonneAGarder: string,
  idPersonneAFusionner: string
): Promise<PersonneWithInfosPrivees> {
  const url = `${Endpoints.PERSONNES}/${idPersonneAGarder}/${Endpoints.FUSION}`;
  return elusApiClient.post(url, { idPersonneAFusionner });
}

export const elusService = {
  getAllPersonnes,
  getPersonneById,
  getPersonneInfosPriveesById,
  getPersonnesByCommune,
  getPersonnesByEpci,
  create,
  patchElu,
  getDeleguesAssembleeGenerale,
  fusionnerPersonnes,
};
